import * as React from 'react';
import { format } from 'd3-format';
import { drawOnCanvas as drawEdge } from '@react-financial-charts/coordinates/lib/EdgeCoordinateV3';
import { getYCoordinate } from '@react-financial-charts/coordinates/lib/MouseCoordinateY';
import type { strokeDashTypes } from '@react-financial-charts/core';
import { GenericChartComponent, getMouseCanvas, getStrokeDasharrayCanvas } from '@react-financial-charts/core';
import type { EdgeConfig, MoreProps } from '@/types/commontypes';

interface LineWithEdgeProps {
  readonly yValue: number;
  readonly strokeStyle: string;
  readonly strokeWidth: number;
  readonly strokeDasharray: strokeDashTypes;
  readonly edge: EdgeConfig;
  readonly visible?: boolean;
  readonly selected?: boolean;
  readonly hovering?: boolean;
  readonly textFill: string;
  readonly fontFamily: string;
  readonly fontSize: number;
  readonly enableDragOnHover?: boolean;
  readonly onDragStart?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDrag?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDragComplete?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onUnHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
}

export class PriceLine extends React.Component<LineWithEdgeProps> {
  static defaultProps = {
    strokeWidth: 1,
    visible: true,
    selected: false,
    hovering: false,
  };

  render() {
    const { onDrag, onDragComplete, onDragStart, onHover, onUnHover, enableDragOnHover } = this.props;
    return (
      <GenericChartComponent
        // clip={false}: edge 태그는 y축 영역에 그려져야 하므로 clip 불가.
        // (GenericComponent defaultProps.clip === true 이므로 명시적으로 false 지정)
        // 서브차트 침범은 drawOnCanvas 내부 y 범위 체크로 방지 (y < 0 || y > height → return).
        clip={false}
        canvasToDraw={getMouseCanvas}
        canvasDraw={this.drawOnCanvas}
        isHover={this.isHover}
        drawOn={['mousemove', 'mouseleave', 'pan', 'drag']}
        enableDragOnHover={enableDragOnHover}
        onHover={onHover}
        onUnHover={onUnHover}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragComplete={onDragComplete}
      />
    );
  }

  private readonly drawOnCanvas = (ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
    if (!this.props.visible) return;
    ctx.save();
    const {
      yValue,
      strokeStyle,
      strokeWidth,
      strokeDasharray,
      edge,
      selected,
      hovering,
      fontFamily,
      fontSize,
      textFill,
    } = this.props;

    const {
      chartConfig: { width, yScale, height },
    } = moreProps;

    const y = Math.round(yScale(yValue));
    if (y < 0 || y > height) {
      ctx.restore();
      return;
    }

    ctx.strokeStyle = strokeStyle;
    ctx.lineWidth = selected || hovering ? strokeWidth + 1 : strokeWidth;
    ctx.setLineDash(getStrokeDasharrayCanvas(strokeDasharray));
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();
    ctx.setLineDash([]);

    const newEdge = {
      ...edge,
      textFill,
      fontFamily,
      fontSize,
    };

    const displayFormat = edge?.displayFormat ?? format('.2f');
    const yValue1 = displayFormat(this.props.yValue);
    const yCoord = getYCoordinate(y, yValue1, newEdge, moreProps);
    drawEdge(ctx, yCoord);
    ctx.restore();
  };

  private readonly isHover = (moreProps: MoreProps) => {
    if (this.props.visible === false) return false;
    const { yValue } = this.props;
    const {
      chartConfig: { yScale },
      mouseXY: [_mouseX, mouseY],
    } = moreProps;
    const y = Math.round(yScale(yValue));
    return Math.abs(mouseY - y) < 4;
  };
}
